/**
 * One vocabulary for what a displayed measure is read on (QA findings R1): WHEN it is read, AGAINST what it is compared and WHOSE it is. A phrase map, not a caption writer: screens
 * keep their own sentences and take these phrases from here, so one basis is worded one way wherever a number appears. Labels only; the DTOs do not carry a basis, so a screen
 * names the basis of the field it prints (`riskFields.ts` says which field is the year-end and which the lowest quarter-end).
 */
import type { KpiId } from "@bsim/domain";
import { READING_PHRASE, VALUE_BASIS_PHRASE, formatMetric } from "@gxs/metric-defs";
import { READS_ON } from "../score/splitWords";

/** OR-30.86: the one wording of the 2-point rate move (the engine owns it so the memo and every screen say it alike). */
export { RATE_MOVE_PHRASE, eveMeasureLabel } from "@bsim/engine";

export type When = "roundStart" | "yearEnd" | "worstQuarterEnd" | "yearAverage" | "cumulative";
export type Against = "lastYear" | "noChange" | "draft" | "draftWithoutCard" | "usualOutcome" | "twin";

export const WHEN_PHRASE: Readonly<Record<When, string>> = {
  roundStart: "at the round start",
  yearEnd: "at year-end",
  worstQuarterEnd: READING_PHRASE.worstQuarterEnd,
  yearAverage: "the year’s average",
  cumulative: "to date",
};

export const AGAINST_PHRASE: Readonly<Record<Against, string>> = {
  lastYear: "against last year",
  noChange: "against the no-change plan",
  draft: "against your draft",
  draftWithoutCard: "against your draft without this card",
  usualOutcome: "against the card’s usual outcome",
  twin: "against your twin",
};


/** "at year-end, against the no-change plan". */
export const basisPhrase = (when: When, against?: Against): string => (against === undefined ? WHEN_PHRASE[when] : `${WHEN_PHRASE[when]}, ${AGAINST_PHRASE[against]}`);

/**
 * The basis each called measure is scored on: the margin and the loan-loss rate are the year's average, marked CET1 the year-end stock. `ForecastStatus.noChangeBaseline` carries the team's own bank's last-year figure
 * on that basis for all three (13.1 §2; OR-30.90). One source for the forecast box, the band picker, the call table and Results.
 */
export const FORECAST_WHEN: Readonly<Record<string, { readonly when: When }>> = {
  nim: { when: "yearAverage" },
  ncoRate: { when: "yearAverage" },
  cet1Marked: { when: "yearEnd" },
};
export const forecastWhen = (kpi: KpiId | string): { readonly when: When } => FORECAST_WHEN[kpi] ?? { when: "yearEnd" };

/** "Last year, your bank’s average" / "Last year, your bank’s year-end": the label of a called measure's last-year figure, saying whose it is and on what. */
export const lastYearWhose = (kpi: KpiId | string): string => `your bank’s ${forecastWhen(kpi).when === "yearAverage" ? "average" : "year-end"}`;
export const lastYearLabel = (kpi: KpiId | string): string => `Last year, ${lastYearWhose(kpi)}`;

/**
 * The readings of one decision’s value, each naming its basis and its horizon (OR-32.1, the words are `VALUE_BASIS_PHRASE`): over three years priced alone on the forecast path (the preview), over three years in the whole plan with one
 * decision put back (the risk officer’s note), the year-end share price only (the one-year leg), and counted on the year as played (the results page). Where two of them meet on a page, `valueBasisLine` says so in one line.
 */
export const VALUE_BASIS = VALUE_BASIS_PHRASE;

/** "Over three years, priced alone on the forecast path +$0.05; counted on the year as played +$0.07": only the readings given, each with its horizon, rounded once to the cent; null when fewer than two, or when they all print the same. */
export function valueBasisLine(v: { readonly alone?: number; readonly inPlan?: number; readonly price?: number; readonly counted?: number }): string | null {
  const parts = (["alone", "inPlan", "price", "counted"] as const).flatMap((k) => { const x = v[k]; return x === undefined ? [] : [{ k, text: formatMetric(x, "usdPerShare", { signed: true }) }]; });
  if (parts.length < 2 || parts.every((p) => p.text === parts[0]?.text)) return null;
  return parts.map((p, i) => { const phrase = `${VALUE_BASIS[p.k]} ${p.text}`; return i === 0 ? phrase.replace(/^./, (c) => c.toUpperCase()) : phrase; }).join("; ");
}

/** What the score’s dollars are read on, said the same wherever a score figure is printed (round-4 G1-N3). */
export const SCORE_UNIT_PHRASE = READS_ON;

/** What a forecast is graded on, said before the year is played (the step) and after it (the result). The no-change plan is your own bank keeping today's settings, not the twin. */
export const FORECAST_GRADED_ON_STEP = "The no-change plan is your own bank as it starts this year, keeping today's settings. Your call is graded on where that plan ends, and Results will show its move in numbers. A call beats 'same as last year' when it is closer to that result than last year's figure was.";
export const FORECAST_GRADED_ON_RESULT = "You are graded on the no-change plan: your own bank as it starts this year, keeping today's settings with nothing changed. Your decisions this year do not move it. The result column shows how your call compares with where that plan ended.";
export const DIRECTION_DEFINITION = "Right direction: the year moved the way you called, up, down or about the same. A little up and a lot up count as the same direction.";
